<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Alert, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from '@sveltestrap/sveltestrap';

  import { CsvPreview } from '@client/components/csv-preview';
  import { FileInput } from '@client/components/file-input';
  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { csvStore } from '@client/stores/csv';

  import { readFile } from '@client/utils/csv';

  import { CSV_UPLOAD_OPTIONS } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  export let show = false;
  export let onUpdate = () => {};

  let sources = deepCopy(CSV_UPLOAD_OPTIONS);
  let selectedSource = sources[0];
  let excludeUsers = false;

  const reset = () => {
    selectedSource = sources[0];

    csvStore.setCsv('');
  };

  const handleFileInput = async (file) => {
    const csvText = await readFile(file);
    csvStore.setCsv(csvText);
  };

  const onSourceChange = () => {
    selectedSource.label = $format(selectedSource.label);

    if (!isDisabled) {
      reset();
    }
  };

  const onHandleManualEntry = (e) => {
    if (!e.target.value) {
      reset();
    }

    csvStore.setCsv(e.target.value);
  };

  const onSave = () => {
    onUpdate({
      data: [...$csvStore.parsed.data],
      errors: [...$csvStore.parsed.errors]
    });
  };

  const toggle = () => {
    reset();
    show = false;
  };

  onMount(onSourceChange);

  $: headerErrors = !(
    $csvStore.parsed?.meta?.fields?.length === 3 &&
    $csvStore.parsed?.meta?.fields?.includes('email/domain') &&
    $csvStore.parsed?.meta?.fields?.includes('campaigns') &&
    $csvStore.parsed?.meta?.fields?.includes('tags')
  );
  $: isDisabled = !$csvStore.parsed?.data?.length || $csvStore.parsed?.errors?.length || headerErrors;
</script>

{#if show}
  <Modal class="modal-wizard" isOpen size="lg" fullscreen="lg" {toggle} {...$$restProps}>
    <ModalHeader class="text-center" {toggle}>
      {$format('label.IMPORT_TARGETING')}
    </ModalHeader>
    <ModalBody>
      <div class="csv-import">
        <div class="section">
          <div class="section-info">
            <h5>
              {$format('label.SOURCE')}
              <Icon id="csv-info-tooltip" name="help-circle" />
              <Tooltip target="csv-info-tooltip">
                {$format('label.TOOLTIP.CSV_SOURCE_TARGETING')}
              </Tooltip>
            </h5>
          </div>
          <div class="section-controls">
            <Select
              id="sync-source"
              items={sources}
              bind:value={selectedSource}
              on:change={onSourceChange}
              placeholder={$format('label.PLACEHOLDER.SELECT_SYNC_SOURCE')}
              clearable={false}
              showChevron
              searchable
            />
          </div>
        </div>
        {#if selectedSource.value === 'csv'}
          <div class="section">
            <div class="section-info">
              <h5>
                {$format('label.SPREADSHEET_FILE')}
                <Icon id="csv-data-tooltip" name="help-circle" />
                <Tooltip target="csv-data-tooltip">{$format('label.TOOLTIP.SPREADSHEET_FILE_GENERIC')}</Tooltip>
              </h5>
            </div>
            <div class="section-controls">
              <FileInput
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                id="csv"
                onLoad={handleFileInput}
                onRemove={reset}
              />
            </div>
          </div>
        {/if}

        {#if selectedSource.value === 'manual'}
          <div class="section">
            <div class="section-info">
              <h5>
                {$format('label.MANUAL_CSV')}
                <Icon id="manual-csv-tooltip" name="help-circle" />
                <Tooltip target="manual-csv-tooltip">{$format('label.TOOLTIP.MANUAL_CSV')}</Tooltip>
              </h5>
            </div>
            <div class="section-controls">
              <Input
                type="textarea"
                class="font-monospace"
                invalid={$csvStore.raw && $csvStore.parsed?.errors?.length}
                placeholder="email/domain,campaigns,tags"
                rows={4}
                on:input={onHandleManualEntry}
                value="email/domain,campaigns,tags"
              />
            </div>
          </div>
        {/if}

        {#if $csvStore?.parsed?.data?.length && headerErrors}
          <Alert color="danger mx-3 my-2 mb-4">
            <h5 class="alert-heading">
              <Icon name="alert-triangle" />
              {$format('label.ERRORS')}
            </h5>
            <p>{$format('label.MESSAGE.CSV_TARGETING_HEADERS')}</p>
          </Alert>
        {/if}

        {#if $csvStore.raw && $csvStore.parsed}
          <div class="csv-preview">
            <CsvPreview
              errors={$csvStore.parsed.errors}
              headers={$csvStore.parsed.meta?.fields}
              rows={$csvStore.parsed.data}
            />
          </div>
        {/if}
      </div>
    </ModalBody>
    <ModalFooter>
      <Button color="link" on:click={toggle}>
        {$format('label.CANCEL')}
      </Button>
      <Button color="primary" disabled={isDisabled} on:click={onSave}>
        {$format('label.UPLOAD_CSV')}
      </Button>
    </ModalFooter>
  </Modal>
{/if}

<style lang="scss" src="./import.scss"></style>
